import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'

type ApiErrorBody = {
  code?: unknown
  data?: unknown
  error?: unknown
  errors?: Record<string, unknown>
  message?: unknown
}

export function getApiErrorCode(error: unknown): string | null {
  return (
    getApiErrorBodies(error)
      .map(({ code }) => code)
      .find(
        (code): code is string =>
          typeof code === 'string' && Boolean(code.trim()),
      ) ?? null
  )
}

function isFetchBaseQueryError(error: unknown): error is FetchBaseQueryError {
  return typeof error === 'object' && error !== null && 'status' in error
}

export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (!isFetchBaseQueryError(error)) {
    return fallback
  }

  if (error.status === 'FETCH_ERROR') {
    return 'Не удалось связаться с сервером. Проверьте соединение и повторите попытку.'
  }

  if (error.status === 'TIMEOUT_ERROR') {
    return 'Сервер отвечает слишком долго. Повторите попытку.'
  }

  const bodies = getApiErrorBodies(error)
  for (const body of bodies) {
    const directMessage = [body.message, body.error].find(
      (value): value is string =>
        typeof value === 'string' && Boolean(value.trim()),
    )
    if (directMessage && isSafeUserMessage(directMessage)) return directMessage
  }

  for (const body of bodies) {
    const firstValidationMessage = Object.values(body.errors ?? {})
      .flatMap((value): unknown[] =>
        Array.isArray(value) ? (value as unknown[]) : [value],
      )
      .find(
        (value): value is string =>
          typeof value === 'string' && Boolean(value.trim()),
      )
    if (firstValidationMessage && isSafeUserMessage(firstValidationMessage)) {
      return firstValidationMessage
    }
  }

  return fallback
}

function isSafeUserMessage(message: string): boolean {
  if (message.length > 500) return false

  return !/(?:PreparedStatementCallback|BadSqlGrammarException|DataIntegrityViolationException|PSQLException|org\.springframework|\bSQL\s*\[|\bERROR:\s|\bDETAIL:\s|foreign key constraint|violates\s+(?:RESTRICT|foreign key|unique|check)|\bconstraint\s+"[^"]+")/iu.test(
    message,
  )
}

function getApiErrorBodies(error: unknown): ApiErrorBody[] {
  if (!isFetchBaseQueryError(error) || !isRecord(error.data)) return []

  const root = error.data as ApiErrorBody
  const nested = isRecord(root.data) ? (root.data as ApiErrorBody) : null

  return nested ? [nested, root] : [root]
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}
